import type { ComponentType } from "react";
import { Suspense, useEffect, useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import { lazily } from "react-lazily";
import { graphql, useFragment } from "react-relay";
import { Routes, Navigate, Route, useParams, Outlet } from "react-router";

import { AdminRoute } from "app/components/AdminRoute/AdminRoute";
import { DetailsSkeleton, TableSkeleton } from "app/components/skeletons";
import { useConfig } from "app/contexts";
import { useAlertContext } from "app/contexts/AlertContext";
import { usePersonalApiKeysEnabled } from "app/hooks/feature-flags/usePersonalApiKeys";
import { skipOnboardingForLocalDev } from "app/utils/localDev";
import { CostDashboardBarStateProvider } from "app/views/dashboards/contexts/CostDashboardBarContext";
import { NotFound } from "app/views/NotFound";
import { CreateNotificationRuleRoot } from "app/views/notifications/rules/create/CreateNotificationRuleRoot";
import { EditNotificationRuleRoot } from "app/views/notifications/rules/edit/EditNotificationRuleRoot";
import { OnboardingContext } from "app/views/onboarding/context/OnboardingContext";
import { GroupsSettingsRoot } from "app/views/settings/access-control/GroupsSettingsRoot";
import { IdentityProvidersSettingsRoot } from "app/views/settings/access-control/IdentityProvidersSettingsRoot";
import { EditGroupRoot } from "app/views/settings/access-control/UpdateGroupForm/EditGroupRoot";
import { UsersSettingsRoot } from "app/views/settings/access-control/UsersSettingsRoot";
import { AccessModelSettingsRoot } from "app/views/settings/access-model/AccessModelSettingsRoot";
import { CostSettingsRoot } from "app/views/settings/cost/CostSettingsRoot";
import { CreateDpcRoot } from "app/views/settings/dynamic-policy-collections/create/CreateDpcRoot";
import { EditDpcRoot } from "app/views/settings/dynamic-policy-collections/edit/EditDpcRoot";

import type { Routes_accounts$key } from "__generated__/Routes_accounts.graphql";
import type { UpsertAWSAccountDiscoveryInput } from "app/mutations/__generated__/UpsertAWSAccountDiscoveryMutation.graphql";

// NOTE: lazy imports must not import from barrels

// onboarding
const { OnboardingWizardRoot } = lazily(
  () => import("app/views/onboarding/OnboardingWizardRoot"),
);

// overview
const { Activity } = lazily(() => import("app/views/overview"));
const { Cloud } = lazily(() => import("app/views/dashboards/cloud/Cloud"));
const { Jun0 } = lazily(() => import("app/views/jun0/Jun0"));

// Conditional Cloud component that redirects if disabled
function ConditionalCloudRoute() {
  const config = useConfig();
  const hiddenDashboardKeys = config?.hidden_dashboard_keys ?? [];
  const isCloudHidden = hiddenDashboardKeys.includes("cloud");

  if (isCloudHidden) {
    return <Navigate to="/activity" replace />;
  }

  return <Cloud />;
}

// Conditional redirect components. `jun0Url` is plumbed in from
// AppRoutes' Routes_accounts fragment — single GraphQL query, no parallel
// useJun0Url hook.
function DefaultDashboardRedirect({ jun0Url }: { jun0Url: string | null }) {
  const config = useConfig();
  const hiddenDashboardKeys = config?.hidden_dashboard_keys ?? [];
  const target = jun0Url
    ? "/jun0"
    : hiddenDashboardKeys.includes("cloud")
      ? "/activity"
      : "/dashboards/cloud";
  return <Navigate to={target} replace />;
}

// Conditional Jun0 component that redirects if backend is absent
function ConditionalJun0Route({ jun0Url }: { jun0Url: string | null }) {
  if (!jun0Url) {
    return <Navigate to="/overview" replace />;
  }
  return <Jun0 jun0Url={jun0Url} />;
}

// Redirect old user details route to new location
function UserDetailsRedirect() {
  const { key } = useParams<"key">();
  return <Navigate to={`/settings/access-control/users/${key}`} replace />;
}

function PolicyUUIDRedirect() {
  const { policyUUID } = useParams<"policyUUID">();
  return <Navigate to={`/admin/policies/${policyUUID}`} replace />;
}

function PolicyVersionRedirect() {
  const { policyUUID, versionNumber } = useParams<
    "policyUUID" | "versionNumber"
  >();
  return (
    <Navigate
      to={`/admin/policies/${policyUUID}/version/${versionNumber}`}
      replace
    />
  );
}

function PolicyExecutionRedirect() {
  const { policyUuid, executionId } = useParams<"policyUuid" | "executionId">();
  return (
    <Navigate
      to={`/admin/policies/${policyUuid}/execution/${executionId}`}
      replace
    />
  );
}
const { PolicyDetailsRoot } = lazily(
  () => import("app/views/overview/policies/details/PolicyDetailsRoot"),
);
const { ExecutionDetailsRoot } = lazily(
  () => import("app/views/overview/policies/details/ExecutionDetailsRoot"),
);

// dashboards
const { CostDashboard } = lazily(
  () => import("app/views/dashboards/cost/CostDashboard"),
);
const { ComplianceDashboard } = lazily(
  () => import("app/views/dashboards/components"),
);
const { CISDashboard } = lazily(
  () => import("app/views/dashboards/cis/CISDashboard"),
);
const { SectionDetails } = lazily(
  () => import("app/views/dashboards/details/SectionDetails"),
);
const { OpportunityDetails } = lazily(
  () => import("app/views/dashboards/cost/details/OpportunityDetails"),
);

// cloudops
const { ResourcesRoot } = lazily(
  () => import("app/views/cloudops/ResourcesRoot"),
);
const { ResourceDetailsRoot } = lazily(
  () => import("app/views/cloudops/resources/details/ResourceDetailsRoot"),
);

// admin
const { PoliciesAndCollectionsRoot } = lazily(
  () => import("app/views/admin/policies/PoliciesAndCollectionsRoot"),
);
const { BindingDetailsRoot } = lazily(
  () => import("app/views/admin/bindings/details/BindingDetailsRoot"),
);
const { BindingsRoot } = lazily(
  () => import("app/views/admin/bindings/BindingsRoot"),
);
const { CreateBindingRoot } = lazily(
  () => import("app/views/admin/bindings/create/CreateBindingRoot"),
);
const { EditBindingRoot } = lazily(
  () => import("app/views/admin/bindings/edit/EditBindingRoot"),
);
const { RunDetailsRoot } = lazily(
  () => import("app/views/admin/bindings/components/RunDetailsRoot"),
);
const { AccountDetailsRoot } = lazily(
  () => import("app/views/admin/accounts/details/AccountDetailsRoot"),
);
const { AccountsAndGroupsRoot } = lazily(
  () => import("app/views/admin/accounts/AccountsAndGroupsRoot"),
);
const { AddAccountRoot } = lazily(
  () => import("app/views/admin/accounts/add/AddAccountRoot"),
);
const { EditAccountRoot } = lazily(
  () => import("app/views/admin/accounts/edit/EditAccountRoot"),
);
const { AccountGroupDetailsRoot } = lazily(
  () =>
    import("app/views/admin/account-groups/details/AccountGroupDetailsRoot"),
);
const { AccountGroupCreateRoot } = lazily(
  () => import("app/views/admin/account-groups/create/AccountGroupCreateRoot"),
);
const { EditAccountGroupRoot } = lazily(
  () => import("app/views/admin/account-groups/edit/EditAccountGroupRoot"),
);
const { CreatePolicyCollectionRoot } = lazily(
  () =>
    import("app/views/admin/policy-collections/create/CreatePolicyCollectionRoot"),
);
const { PolicyCollectionDetails } = lazily(
  () =>
    import("app/views/admin/policy-collections/details/PolicyCollectionDetails"),
);
const { EditPolicyCollectionRoot } = lazily(
  () =>
    import("app/views/admin/policy-collections/edit/EditPolicyCollectionRoot"),
);

// notifications
const { IntegrationsRoot } = lazily(
  () => import("app/views/notifications/integrations/IntegrationsRoot"),
);
const { SlackRoot } = lazily(
  () => import("app/views/notifications/integrations/slack/SlackRoot"),
);
const { EmailRoot } = lazily(
  () => import("app/views/notifications/integrations/email/EmailRoot"),
);
const { JiraRoot } = lazily(
  () => import("app/views/notifications/integrations/jira/JiraRoot"),
);
const { CreateJiraInstanceRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/jira/create/CreateJiraInstanceRoot"),
);
const { EditJiraInstanceRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/jira/edit/EditJiraInstanceRoot"),
);
const { ServiceNowRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/service-now/ServiceNowRoot"),
);
const { TeamsRoot } = lazily(
  () => import("app/views/notifications/integrations/teams/TeamsRoot"),
);
const { SetupTeamsRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/teams/setup/SetupTeamsRoot"),
);
const { TeamsChannelMappingRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/teams/components/TeamsChannelMapping/TeamsChannelMappingRoot"),
);
const { PersonalApiKeysCreateRoot } = lazily(
  () =>
    import("app/views/settings/personal-api-keys/create/PersonalApiKeysCreateRoot"),
);
const { PersonalApiKeysEditRoot } = lazily(
  () =>
    import("app/views/settings/personal-api-keys/edit/PersonalApiKeysEditRoot"),
);
const { NotificationsOverviewRoot } = lazily(
  () => import("app/views/notifications/overview"),
);
const { SettingsRoot } = lazily(
  () => import("app/views/notifications/settings/SettingsRoot"),
);
const { AddTemplateRoot } = lazily(
  () => import("app/views/notifications/settings/templates/AddTemplateRoot"),
);
const { EditTemplateRoot } = lazily(
  () => import("app/views/notifications/settings/templates/EditTemplateRoot"),
);

// settings
const { RepositoryDetailsRoot } = lazily(
  () => import("app/views/admin/repositories/details/RepositoryDetailsRoot"),
);
const { LegacyPoliciesRoot } = lazily(
  () => import("app/views/admin/repositories/details/LegacyPoliciesRoot"),
);
const { UserDetailsRoot } = lazily(
  () => import("app/views/settings/users/user-list/details/UserDetailsRoot"),
);
const { UserEditRoot } = lazily(
  () => import("app/views/settings/users/user-list/details/UserEditRoot"),
);
const { ProfileSettingsRoot } = lazily(
  () => import("app/views/settings/profile/ProfileSettingsRoot"),
);
const { AddIdentityProviderRoot } = lazily(
  () =>
    import("app/views/settings/access-control/AddIdentityProvider/AddIdentityProviderRoot"),
);
const { AddGroupRoot } = lazily(
  () =>
    import("app/views/settings/access-control/UpdateGroupForm/AddGroupRoot"),
);
const { EditIdentityProviderRoot } = lazily(
  () =>
    import("app/views/settings/access-control/EditIdentityProvider/EditIdentityProviderRoot"),
);
const { GroupDetailsRoot } = lazily(
  () =>
    import("app/views/settings/access-control/GroupDetails/GroupDetailsRoot"),
);
const { IdentityProviderDetailsRoot } = lazily(
  () =>
    import("app/views/settings/access-control/IdentityProviderDetails/IdentityProviderDetailsRoot"),
);

const { EditRepositoryConfigRoot } = lazily(
  () => import("app/views/admin/repositories/edit/EditRepositoryConfigRoot"),
);

const { AddRepositoryConfigRoot } = lazily(
  () => import("app/views/admin/repositories/add/AddRepositoryConfigRoot"),
);

const { NotificationRulesRoot } = lazily(
  () => import("app/views/notifications/rules/NotificationRulesRoot"),
);
const { NotificationRulesDetailsRoot } = lazily(
  () =>
    import("app/views/notifications/rules/details/NotificationRulesDetailsRoot"),
);
const { NotificationGroupDetailsRoot } = lazily(
  () =>
    import("app/views/notifications/rules/group-details/NotificationGroupDetailsRoot"),
);

const { CloudsRoot } = lazily(
  () => import("app/views/settings/clouds/CloudsRoot"),
);
const { GcpDetailsRoot } = lazily(
  () => import("app/views/settings/clouds/gcp/details/GcpDetailsRoot"),
);
const { GcpDeploymentRoot } = lazily(
  () => import("app/views/settings/clouds/gcp/deployment/GcpDeploymentRoot"),
);

// api
const { Sandbox } = lazily(() => import("app/views/sandbox/Sandbox"));

interface DashboardContentProps {
  CostComponent: ComponentType;
  ComplianceComponent: ComponentType;
}
function DashboardContent({
  CostComponent,
  ComplianceComponent,
}: DashboardContentProps) {
  const { key, sectionKey } = useParams();

  if (key?.startsWith("cis-") && !sectionKey) {
    return <Navigate to={`/dashboards/cis?version=${key}`} replace />;
  }

  if (key?.includes("cost")) {
    return (
      <CostDashboardBarStateProvider>
        <CostComponent />
      </CostDashboardBarStateProvider>
    );
  }

  return <ComplianceComponent />;
}

function AdminLayout() {
  return (
    <AdminRoute>
      <Outlet />
    </AdminRoute>
  );
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

interface Props {
  queryRef: Routes_accounts$key;
}

export function AppRoutes({ queryRef }: Props) {
  const [orgDetails, setOrgDetails] =
    useState<UpsertAWSAccountDiscoveryInput | null>(null);

  const orgData = useMemo(() => ({ orgDetails, setOrgDetails }), [orgDetails]);

  const data = useFragment(
    graphql`
      fragment Routes_accounts on Query {
        accounts {
          problems {
            message
          }
          pageInfo {
            total
          }
        }
        UrlConfig {
          jun0
        }
        whoami {
          __typename
          config {
            isOnboardingSkipped
          }
        }
      }
    `,
    queryRef,
  );

  const { alertDispatch } = useAlertContext();
  const config = useConfig();

  const problems = data.accounts.problems;

  useEffect(() => {
    problems.forEach((problem) => {
      alertDispatch({
        message: problem.message,
        severity: "error",
      });
    });
  }, [problems, alertDispatch]);

  const hasProblems = data.accounts.problems.length > 0;
  const hasAccounts = (data.accounts.pageInfo.total ?? 0) > 0;
  // Plumbed in via Routes_accounts so we don't need a parallel useJun0Url query.
  const jun0Url = data.UrlConfig?.jun0 ?? null;

  const personalApiKeysEnabled = usePersonalApiKeysEnabled();

  if (
    !hasAccounts &&
    !hasProblems &&
    !data.whoami.config?.isOnboardingSkipped &&
    // Local-dev convenience: the stack rarely has an AWS account seeded, so the
    // onboarding gate would otherwise trap every route (see app/utils/localDev).
    !skipOnboardingForLocalDev()
  ) {
    return (
      <Routes>
        <Route element={<Navigate to="/onboarding" />} path="/*" />
        <Route path="onboarding">
          <Route
            element={
              <OnboardingContext.Provider value={orgData}>
                <OnboardingWizardRoot />
              </OnboardingContext.Provider>
            }
            index
          />
        </Route>
      </Routes>
    );
  }

  return (
    <SentryRoutes>
      {/* /onboarding */}
      <Route path="onboarding">
        <Route
          element={
            <OnboardingContext.Provider value={orgData}>
              <OnboardingWizardRoot />
            </OnboardingContext.Provider>
          }
          index
        />
      </Route>

      {/* /overview */}
      <Route element={<Navigate to="/overview" />} index />
      <Route path="setup">
        <Route element={<Navigate to="/overview" />} index />
      </Route>
      <Route path="overview">
        <Route element={<DefaultDashboardRedirect jun0Url={jun0Url} />} index />
      </Route>

      {/* /activity */}
      <Route path="activity">
        <Route element={<Activity />} index />
      </Route>

      {/* /jun0 */}
      <Route path="jun0">
        <Route element={<ConditionalJun0Route jun0Url={jun0Url} />} index />
      </Route>

      {/* /dashboards */}
      <Route path="dashboards">
        <Route element={<DefaultDashboardRedirect jun0Url={jun0Url} />} index />
        <Route path="cloud">
          <Route element={<ConditionalCloudRoute />} index />
        </Route>
        <Route path="cis">
          <Route element={<CISDashboard />} index />
        </Route>
        <Route
          element={
            <DashboardContent
              ComplianceComponent={ComplianceDashboard}
              CostComponent={CostDashboard}
            />
          }
          path=":key"
        />
        <Route
          element={
            <DashboardContent
              ComplianceComponent={SectionDetails}
              CostComponent={OpportunityDetails}
            />
          }
          path=":key/details/:sectionKey"
        />
      </Route>

      {/* read-only (/bindings, /accounts, /account-groups, /policy-collections) */}
      <Route path="bindings">
        <Route element={<BindingDetailsRoot readOnly />} path=":bindingUUID" />
      </Route>
      <Route path="accounts">
        <Route element={<AccountDetailsRoot readOnly />} path=":accountKey" />
      </Route>
      <Route path="account-groups">
        <Route
          element={<AccountGroupDetailsRoot readOnly />}
          path=":accountGroupUUID"
        />
      </Route>
      <Route path="policy-collections">
        <Route
          element={<PolicyCollectionDetails readOnly />}
          path=":collectionId"
        />
      </Route>

      {/* /policies — redirects to admin */}
      <Route path="policies">
        <Route element={<Navigate to="/admin/policies" replace />} index />
        <Route path=":policyUUID">
          <Route element={<PolicyUUIDRedirect />} index />
          <Route
            element={<PolicyVersionRedirect />}
            path="version/:versionNumber"
          />
        </Route>
        <Route
          element={<PolicyExecutionRedirect />}
          path=":policyUuid/execution/:executionId"
        />
      </Route>

      {/* /cloudops */}
      <Route path="cloudops">
        <Route element={<Navigate to="/cloudops/resources" replace />} index />
        {/* legacy /cloudops/resource-matches → Matches tab */}
        <Route
          element={<Navigate to="/cloudops/resources?tab=matches" replace />}
          path="resource-matches"
        />
        <Route path="resources">
          <Route element={<ResourcesRoot />} index />
          <Route element={<ResourceDetailsRoot />} path=":resourceKey" />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <ExecutionDetailsRoot />
              </Suspense>
            }
            path=":resourceKey/:executionId"
          />
        </Route>
      </Route>

      {/* /admin */}
      <Route path="admin">
        <Route element={<Navigate to="bindings" />} index />
        {/* /admin/accounts */}
        <Route path="accounts">
          <Route element={<AccountsAndGroupsRoot />} index />
          <Route
            element={
              <AdminRoute>
                <Suspense fallback={<DetailsSkeleton searchBar />}>
                  <AddAccountRoot />
                </Suspense>
              </AdminRoute>
            }
            path="add"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton searchBar />}>
                <AccountDetailsRoot />
              </Suspense>
            }
            path="details/:accountProvider/:accountKey"
          />
          <Route
            element={
              <AdminRoute>
                <Suspense fallback={<DetailsSkeleton searchBar />}>
                  <EditAccountRoot />
                </Suspense>
              </AdminRoute>
            }
            path="details/:accountProvider/:accountKey/edit"
          />
        </Route>

        {/* /admin/policy-collections */}
        <Route path="policy-collections">
          <Route
            element={
              <Navigate to="/admin/policies?tab=policy-collections" replace />
            }
            index
          />
          <Route element={<CreatePolicyCollectionRoot />} path="create" />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <PolicyCollectionDetails />
              </Suspense>
            }
            path=":collectionId"
          />
          <Route
            element={<EditPolicyCollectionRoot />}
            path=":collectionId/edit"
          />

          <Route path="dynamic">
            <Route element={<CreateDpcRoot />} path="create" />
            <Route element={<EditDpcRoot />} path="edit" />
          </Route>
        </Route>

        {/* /admin/account-groups */}
        <Route path="account-groups">
          <Route
            element={
              <Navigate to="/admin/accounts?tab=account-groups" replace />
            }
            index
          />
          <Route element={<AccountGroupCreateRoot />} path="create" />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <AccountGroupDetailsRoot />
              </Suspense>
            }
            path=":accountGroupUUID"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <EditAccountGroupRoot />
              </Suspense>
            }
            path=":accountGroupUUID/edit"
          />
        </Route>

        {/* /admin/bindings */}
        <Route path="bindings">
          <Route element={<BindingsRoot />} index />
          <Route
            element={
              // TODO: This route should 404 if user does not have _any_ deployable account groups
              // https://stacklet.atlassian.net/browse/ENG-5231
              <Suspense
                fallback={<TableSkeleton size="large" topMargin={16} />}
              >
                <CreateBindingRoot />
              </Suspense>
            }
            path="create"
          />

          {/* /admin/bindings/:uuid */}
          <Route path=":bindingUUID">
            <Route
              element={
                <Suspense fallback={<DetailsSkeleton />}>
                  <BindingDetailsRoot />
                </Suspense>
              }
              index
            />
            <Route
              element={
                // TODO: This route should 404 if user does not have _any_ deployable account groups
                // https://stacklet.atlassian.net/browse/ENG-5231
                <EditBindingRoot />
              }
              path="edit"
            />

            {/* /admin/bindings/:uuid/run */}
            <Route path="run">
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <RunDetailsRoot />
                  </Suspense>
                }
                path=":runID"
              />
            </Route>
          </Route>
        </Route>

        {/* /admin/policies */}
        <Route path="policies">
          <Route element={<PoliciesAndCollectionsRoot />} index />
          <Route path=":policyUUID">
            <Route
              element={
                <Suspense fallback={<DetailsSkeleton />}>
                  <PolicyDetailsRoot />
                </Suspense>
              }
              index
            />
            <Route
              element={
                <Suspense fallback={<DetailsSkeleton />}>
                  <PolicyDetailsRoot />
                </Suspense>
              }
              path="version/:versionNumber"
            />
          </Route>
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <ExecutionDetailsRoot />
              </Suspense>
            }
            path=":policyUuid/execution/:executionId"
          />
        </Route>

        {/* /admin/repositories */}
        <Route path="repositories">
          <Route
            element={<Navigate to="/admin/policies?tab=repositories" replace />}
            index
          />
          <Route element={<AddRepositoryConfigRoot />} path="add" />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <RepositoryDetailsRoot />
              </Suspense>
            }
            path=":repositoryUUID"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <EditRepositoryConfigRoot />
              </Suspense>
            }
            path=":repositoryUUID/edit"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <LegacyPoliciesRoot />
              </Suspense>
            }
            path=":repositoryUUID/legacy-policies"
          />
        </Route>
      </Route>

      {/* /notifications */}
      <Route element={<AdminLayout />} path="notifications">
        <Route element={<Navigate to="overview" replace />} index />
        <Route element={<NotificationsOverviewRoot />} path="overview" />

        {/* /notifications/settings */}
        <Route path="settings">
          <Route element={<SettingsRoot />} index />

          {/* /notifications/settings/templates */}
          <Route path="templates">
            <Route element={<AddTemplateRoot />} path="add" />
            <Route element={<EditTemplateRoot />} path=":name" />
          </Route>
        </Route>

        <Route path="rules">
          <Route element={<NotificationRulesRoot />} index />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <CreateNotificationRuleRoot />
              </Suspense>
            }
            path="create"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <EditNotificationRuleRoot />
              </Suspense>
            }
            path=":ruleUUID/edit"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <NotificationGroupDetailsRoot />
              </Suspense>
            }
            path=":ruleUUID/issues/:groupId"
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <NotificationRulesDetailsRoot />
              </Suspense>
            }
            path=":ruleUUID"
          />
        </Route>
      </Route>

      {/* /settings */}
      <Route path="settings">
        <Route element={<Navigate to="profile" replace />} index />

        <Route path="profile">
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <ProfileSettingsRoot />
              </Suspense>
            }
            index
          />
          <Route
            element={
              <Suspense fallback={<DetailsSkeleton />}>
                <UserEditRoot />
              </Suspense>
            }
            path="edit"
          />
        </Route>

        <Route element={<AdminLayout />}>
          {/* /settings/cost */}
          <Route path="cost">
            <Route element={<CostSettingsRoot />} index />
          </Route>

          {/* /settings/integrations */}
          <Route path="integrations">
            <Route element={<IntegrationsRoot />} index />
            <Route element={<SlackRoot />} path="slack" />
            <Route element={<EmailRoot />} path="email" />
            <Route path="jira">
              <Route element={<JiraRoot />} index />
              <Route element={<CreateJiraInstanceRoot />} path="create" />
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <EditJiraInstanceRoot />
                  </Suspense>
                }
                path=":uuid/edit"
              />
            </Route>
            <Route element={<ServiceNowRoot />} path="service-now" />
            <Route path="teams">
              <Route element={<TeamsRoot />} index />
              <Route element={<SetupTeamsRoot />} path="setup" />
              <Route
                element={<TeamsChannelMappingRoot />}
                path="channel-mapping"
              />
            </Route>
          </Route>

          {/* /settings/personal-api-keys */}
          {personalApiKeysEnabled ? (
            <Route path="personal-api-keys">
              <Route element={<PersonalApiKeysCreateRoot />} path="create" />
              <Route
                element={<PersonalApiKeysEditRoot />}
                path="edit/:identity"
              />
            </Route>
          ) : null}

          {/* /settings/access-control */}
          <Route path="access-control">
            <Route element={<Navigate to="users" replace />} index />
            <Route path="identity-providers">
              <Route element={<IdentityProvidersSettingsRoot />} index />
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <AddIdentityProviderRoot />
                  </Suspense>
                }
                path="add"
              />
              {/* Ranked below "add", so a provider named "add" is unreachable */}
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <IdentityProviderDetailsRoot />
                  </Suspense>
                }
                path=":providerName"
              />
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <EditIdentityProviderRoot />
                  </Suspense>
                }
                path=":providerName/edit"
              />
            </Route>
            <Route path="users">
              <Route element={<UsersSettingsRoot />} index />
              <Route path=":key">
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <UserDetailsRoot />
                    </Suspense>
                  }
                  index
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <UserEditRoot />
                    </Suspense>
                  }
                  path="edit"
                />
              </Route>
            </Route>
            <Route path="groups">
              <Route element={<GroupsSettingsRoot />} index />
              <Route
                element={
                  <Suspense fallback={<DetailsSkeleton />}>
                    <AddGroupRoot />
                  </Suspense>
                }
                path="add"
              />
              <Route path=":groupUuid">
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <GroupDetailsRoot />
                    </Suspense>
                  }
                  index
                />
                <Route element={<EditGroupRoot />} path="edit" />
              </Route>
            </Route>
          </Route>

          {/* Redirect old user details route to new location */}
          <Route element={<UserDetailsRedirect />} path="users/:key" />

          {config?.dev_flags.includes("admin-settings") ? (
            <Route path="access-model">
              <Route element={<AccessModelSettingsRoot />} index />
            </Route>
          ) : null}

          {/* /settings/clouds */}
          <Route path="clouds">
            <Route element={<CloudsRoot />} index />
            <Route path="gcp">
              <Route element={<GcpDetailsRoot />} path="details" />
              <Route element={<GcpDeploymentRoot />} path="deployment" />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* /api */}
      <Route element={<Sandbox />} path="api" />

      <Route element={<NotFound />} path="*" />
    </SentryRoutes>
  );
}
